"use client";

import { LocationIcon } from "@/components/icons/Location";
import { Skeleton } from "@/components/ui/Skeleton";
import { useLocationStore } from "@/lib/hooks";
import { useEffect } from "react";
import styles from "./RetailerLocation.module.css";

export interface RetailerLocationProps {
	coordinates?: [number, number];
}

const calculateDistance = (coord1: [number, number], coord2: [number, number]): number => {
	const [lon1, lat1] = coord1;
	const [lon2, lat2] = coord2;

	const R = 6371; // Radius of the Earth in kilometers
	const dLat = ((lat2 - lat1) * Math.PI) / 180;
	const dLon = ((lon2 - lon1) * Math.PI) / 180;

	const a =
		Math.sin(dLat / 2) * Math.sin(dLat / 2) +
		Math.cos((lat1 * Math.PI) / 180) *
			Math.cos((lat2 * Math.PI) / 180) *
			Math.sin(dLon / 2) *
			Math.sin(dLon / 2);

	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	const distance = R * c;

	return distance;
};

export const RetailerLocation = ({ coordinates: retailerCoordinates }: RetailerLocationProps) => {
	const { coordinates: userCoordinates, setCoordinates } = useLocationStore();

	useEffect(() => {
		if ("geolocation" in navigator) {
			navigator.geolocation.getCurrentPosition(
				(position) => {
					const coordinates: [number, number] = [
						position.coords.latitude,
						position.coords.longitude,
					];
					setCoordinates(coordinates);
				},
				() => {},
				{
					enableHighAccuracy: true,
					timeout: 5000,
					maximumAge: 0,
				},
			);
		}
	}, [setCoordinates]);

	const distanceInKmFromRetailerCoordinatesToUserCoordinates =
		retailerCoordinates && userCoordinates
			? calculateDistance(retailerCoordinates, userCoordinates)
			: null;

	return (
		<div className={styles.root}>
			{distanceInKmFromRetailerCoordinatesToUserCoordinates && (
				<>
					<LocationIcon />
					<span className={styles.km}>
						{distanceInKmFromRetailerCoordinatesToUserCoordinates?.toFixed(2)} km
					</span>
				</>
			)}
		</div>
	);
};

export const RetailerLocationSkeleton = () => {
	return (
		<div className={styles.root}>
			<Skeleton fitContent>
				<LocationIcon />
			</Skeleton>
			<Skeleton fitContent>
				<span className={styles.km}>1.00 km</span>
			</Skeleton>
		</div>
	);
};
